/** Small colour helpers. Themes store hex strings; the renderer needs to bend them. */

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

const cache = new Map<string, Rgb>();

export function parseHex(hex: string): Rgb {
  const hit = cache.get(hex);
  if (hit) return hit;
  let h = hex.trim().replace('#', '');
  if (h.length === 3) h = h[0]! + h[0]! + h[1]! + h[1]! + h[2]! + h[2]!;
  const n = Number.parseInt(h, 16);
  const rgb: Rgb = { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  cache.set(hex, rgb);
  return rgb;
}

export function rgba(hex: string, alpha: number): string {
  const c = parseHex(hex);
  return `rgba(${c.r},${c.g},${c.b},${clamp01(alpha)})`;
}

export function mixHex(a: string, b: string, t: number): string {
  const x = parseHex(a);
  const y = parseHex(b);
  const k = clamp01(t);
  return `rgb(${Math.round(x.r + (y.r - x.r) * k)},${Math.round(x.g + (y.g - x.g) * k)},${Math.round(
    x.b + (y.b - x.b) * k,
  )})`;
}

/** Positive brightens toward white, negative darkens toward black. */
export function shade(hex: string, amount: number): string {
  return amount >= 0 ? mixHex(hex, '#ffffff', amount) : mixHex(hex, '#000000', -amount);
}

export function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Cheap deterministic hash-noise, for prop wobble that must not flicker. */
export function noise(n: number): number {
  const s = Math.sin(n * 127.1) * 43758.5453;
  return s - Math.floor(s);
}

export function hsl(h: number, s: number, l: number, a = 1): string {
  return `hsla(${((h % 360) + 360) % 360},${s}%,${l}%,${a})`;
}
